<template>
  <div>
    <confirm-modal
      v-if="showExcelDownloadModal"
      id="excel-download-modal"
      title="Information"
      :confirmation="confirmModalMessage"
      ok-button="Close"
      close-button=""
      @submit="showExcelDownloadModal = false"
    />
    <edit-h1-parcel-items
      v-if="selectedEditParcel && declarationType === 'h1'"
      id="edit-h1-parcel-items"
      :parcel="selectedEditParcel"
      :declaration="computedDeclarationData"
      :shipment="shipment"
      :readonly="!canEdit"
    />
    <edit-parcel-items
      v-if="selectedEditParcel && declarationType !== 'h1'"
      id="edit-parcel-items"
      :parcel="selectedEditParcel"
      :declaration="computedDeclarationData"
      :shipment="shipment"
      :readonly="!canEdit"
    />
    <status-information
      v-if="informationItem"
      :item="informationItem"
    />
    <b-row>
      <b-col class="mt-2">
        <b-card-text>
          Parcels need additional information. Please add comment and attachment(s).
        </b-card-text>
      </b-col>
      <b-col
        class="text-right"
      >
        <b-button
          variant="outline-success"
          class="m-1"
          @click="handleExcelDownload"
        >
          Download as Excel
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <div class="overflow-x-scroll">
          <b-table
            :items="parcels"
            responsive
            :fields="fields"
            :busy="loading"
            show-empty
            empty-text="No matching records found"
            class="mb-0"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-1" />
                <strong>Loading...</strong>
              </div>
            </template>

            <template #cell(actions)="data">
              <view-parcel-button
                v-if="!canEdit"
                :declaration="data.item.low_value_declaration"
                :parcel="data.item"
              />
              <b-button
                v-if="canEdit"
                type="button"
                variant="warning"
                @click="editDeclaration(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
            </template>

            <template #cell(tracking_number)="data">
              {{ (data.item.goods_shipment || data.item.parcel || {}).tracking_number }}
            </template>

            <template #cell(container_code)="data">
              {{ (data.item.goods_shipment || data.item.parcel || {}).container_code }}
            </template>

            <template #cell(descriptions)="data">
              <div>
                {{ (data.item.goods_shipment || data.item.parcel || {}).items ? truncatedDescriptions((data.item.goods_shipment || data.item.parcel || {}).items) : truncatedList((data.item.goods_shipment || data.item.parcel || {}).descriptions_preview) }}
              </div>
            </template>

            <template #cell(hs_codes)="data">
              <div v-if="(data.item.goods_shipment || data.item.parcel || {}).items">
                <b-row
                  v-for="(parcelItem, index) in (data.item.goods_shipment || data.item.parcel || {items: []}).items"
                  :key="index"
                >
                  <span
                    v-if="hasItemError(parcelItem, data.item.status_info)"
                    style="color: #780000; font-weight: bold;"
                  >
                    {{ parcelItem.hs_code }}
                  </span>
                  <span v-else>{{ parcelItem.hs_code }}</span>
                </b-row>
              </div>
              <div v-else>
                {{ truncatedList((data.item.goods_shipment || data.item.parcel || {}).hs_codes_preview) }}
              </div>
            </template>

            <template #cell(value)="data">
              <div
                v-if="parcelValue(data.item) !== null"
                class="text-right"
              >
                {{ parcelValue(data.item) | formatNumber(2) }}
              </div>
            </template>

            <template #cell(status_info)="data">
              <div v-if="data.item.status_info !== null && data.item.status_info['errors']">
                <span
                  v-b-modal.modal-status-information
                  class="text-primary"
                  @click="informationItem = data.item"
                >
                  Show errors
                  <b-badge
                    variant="light"
                    class="text-primary text-nowrap"
                  >
                    {{ data.item.status_info['errors'].length }}
                  </b-badge>
                </span>
              </div>
            </template>

          </b-table>
        </div>
        <pagination
          v-show="!loading"
          :per-page="perPage"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCardText,
  BCol,
  BRow,
  BTable,
  BButton,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'
import EditParcelItems from '@/views/shipments/declarations/modals/EditParcelItems.vue'
import EditH1GoodsItems from '@/views/shipments/declarations/modals/EditH1GoodsItems.vue'
import Pagination from '@/layouts/components/Pagination/Pagination.vue'
import StatusInformation from '@/views/shipments/declarations/modals/StatusInformation.vue'
import ViewParcelButton from '@/views/shipments/declarations/components/ViewParcelButton.vue'
import { EventBus, truncatedDescriptions, truncatedList, resolveGoodsShipmentValue } from '@core/utils/utils'
import { searchQuery } from '@core/utils/filter'
import ConfirmModal from '@core/components/confirmation/confirmModal.vue'
import excelDownloadMixin from '@/mixins/excelDownloadMixin'
import moment from 'moment'

export default {
  components: {
    ConfirmModal,
    ViewParcelButton,
    BBadge,
    StatusInformation,
    Pagination,
    BCardText,
    BCol,
    BRow,
    BTable,
    BButton,
    BSpinner,
    EditParcelItems,
    EditH1ParcelItems: EditH1GoodsItems,
  },
  mixins: [excelDownloadMixin],
  props: {
    shipment: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      default: () => {},
    },
    declarationType: {
      type: String,
      default: 'h7',
    },
  },
  data() {
    return {
      loading: false,
      informationItem: null,
      page: 1,
      perPage: 100,
      selectedEditParcel: null,
      selectedEditDeclaration: null,
      excelDownloadUrl: `${process.env.VUE_APP_API_URL}/v1/companies/${this.shipment.company.id}/shipments/${this.shipment.id}/declarations/${this.declarationType}/needsDocuments/excel`,
      fields: [
        { key: 'actions', label: 'Actions' },
        { key: 'tracking_number', label: 'Tracking code' },
        { key: 'container_code', label: 'Container' },
        { key: 'descriptions', label: 'Description' },
        { key: 'hs_codes', label: 'HS code' },
        { key: 'value', label: 'Value' },
        { key: 'lrn', label: 'LRN' },
        { key: 'mrn', label: 'MRN' },
        { key: 'status_info', label: 'Errors' },
      ],
      parcels: [],
    }
  },
  computed: {
    excelFileName() {
      const timestamp = moment().format('YYYY-MM-DD-HH-mm-ss')
      return `needs-documents-declarations-list-${timestamp}.xlsx`
    },
    canEdit() {
      return this.$permissions().hasPermission('edit shipment')
    },
    computedDeclarationData() {
      if (this.selectedEditDeclaration !== null) {
        return this.selectedEditDeclaration
      }
      return null
    },
    url() {
      let url = `/v1/companies/${this.$activeCompany().data.company.id}/shipments/${this.shipment.id}/declarations/${this.declarationType}/needs-documents`
      url += `?page=${this.page}`
      url += `&per_page=${this.perPage}`
      return url
    },
  },
  watch: {
    filters() {
      this.fetchData()
    },
  },
  created() {
    EventBus.$on('update-parcel-items', this.updateParcelItems)
    EventBus.$on('general-declaration-update', this.fetchData)
    EventBus.$on('set-view-parcel', this.viewParcel)
    this.fetchData()
  },
  mounted() {
    EventBus.$on('renewDeclarationList', this.handleRenewDeclarationList)
    EventBus.$on('pageChange', this.handlePageChange)
  },
  beforeDestroy() {
    EventBus.$off('update-parcel-items', this.updateParcelItems)
    EventBus.$off('general-declaration-update', this.fetchData)
    EventBus.$off('set-view-parcel', this.viewParcel)
    EventBus.$off('renewDeclarationList', this.handleRenewDeclarationList)
    EventBus.$off('pageChange', this.handlePageChange)
  },
  methods: {
    handlePageChange(newPage) {
      this.page = newPage
      this.fetchData()
    },
    handleRenewDeclarationList() {
      this.fetchData()
      EventBus.$emit('refresh-statistics')
    },
    hasItemError(item, statusInfo) {
      if (!statusInfo || !statusInfo.errors) return false
      return statusInfo.errors.some(error => item?.id != null
        && error?.item_reference != null
        && String(error.item_reference) === String(item.id))
    },
    updateParcelItems(parcelId) {
      this.$http.put(`/v1/companies/${this.$activeCompany().data.company.id}/shipments/${this.shipment.id}/declarations`, { goodsShipments: [parcelId] })
      this.selectedEditParcel = null
      this.selectedEditDeclaration = null
      this.fetchData()
      EventBus.$emit('refresh-statistics')
    },
    viewParcel(declaration, parcel) {
      this.selectedEditDeclaration = declaration
      this.selectedEditParcel = parcel
      this.$nextTick(() => {
        this.$bvModal.show(this.declarationType === 'h1' ? 'edit-h1-parcel-items' : 'edit-parcel-items')
      })
    },
    editDeclaration(data) {
      this.selectedEditParcel = data.goods_shipment || data.parcel
      this.selectedEditDeclaration = data
      this.$nextTick(() => {
        this.$bvModal.show(this.declarationType === 'h1' ? 'edit-h1-parcel-items' : 'edit-parcel-items')
      })
    },
    truncatedDescriptions,
    truncatedList,
    parcelValue(item) {
      const goodsShipment = item.goods_shipment || item.parcel || {}
      return resolveGoodsShipmentValue(goodsShipment)
    },
    fetchData(activeTab, showLoader = true) {
      if ((!activeTab || activeTab.startsWith('needs-documents')) && !this.loading) {
        if (showLoader) {
          this.loading = true
        }
        this.$http.get(searchQuery(this.url, this.filters))
          .then(response => {
            this.loading = false
            this.parcels = response.data.data
            EventBus.$emit('updateMeta', response.data.meta)
          })
      }
    },
  },
}
</script>

<style scoped>

</style>
